// Libs
import classNames from 'classnames/bind';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid2 } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
// Components, Layouts, Pages
import { BaseInput, BaseInputEmailMultiple, BaseTextEditor, FileUpload, Loading } from '~/components/common';
// Others
import { ModeTextEditorEnum } from '~/utils/enum/common';
import { IFormSendGmail } from '~/utils/interface/google';
import { defaultValuesForm, schema } from './helper';
import { ACCEPT_ALL_FILES } from '~/utils/constants/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { createFormData } from '~/utils/helper/common';
import { sendGoogleGmail } from '~/thunks/google/googleThunk';
// Styles, images, icons
import styles from './FormSendGmail.module.scss';

type Props = {
  onClose: () => void;
};

const cx = classNames.bind(styles);

const FormSendEmail = (props: Props) => {
  //#region Destructuring Props
  const { onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/googleEmail']);
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IFormSendGmail>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseForm = () => {
    onClose && onClose();
    reset(defaultValuesForm);
  };

  const handleSendGmail = async (values: IFormSendGmail) => {
    setIsLoading(true);

    const formDataFields = {
      ...values,
      to: (values?.to || []).join(', '),
      cc: (values?.cc || []).join(', '),
      bcc: (values?.bcc || []).join(', '),
    };

    const body = await createFormData(formDataFields, 'files');
    dispatch(sendGoogleGmail(body))
      .unwrap()
      .then((_res) => {
        handleCloseForm();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };
  //#endregion Handle Function

  return (
    <Dialog id='formSendGmailComponent' open={true} fullWidth maxWidth={'md'} onClose={handleCloseForm}>
      <DialogTitle>{t('form_send_google_gmail_title')}</DialogTitle>

      <Box component={'form'} onSubmit={handleSubmit(handleSendGmail)} className={cx('form')}>
        <DialogContent dividers={true} className={cx('contentForm')}>
          <Grid2 container size={12} rowSpacing={'24px'} columnSpacing={'24px'}>
            <Grid2 size={4}>
              <Controller
                name='to'
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <BaseInputEmailMultiple
                      label={t('form_send_google_gmail_to_label')}
                      value={value}
                      onChange={onChange}
                      required
                      errorMessage={errors.to?.message}
                    />
                  );
                }}
              />
            </Grid2>

            <Grid2 size={4}>
              <Controller
                name='cc'
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <BaseInputEmailMultiple
                      label={t('form_send_google_gmail_cc_label')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors.cc?.message}
                    />
                  );
                }}
              />
            </Grid2>

            <Grid2 size={4}>
              <Controller
                name='bcc'
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <BaseInputEmailMultiple
                      label={t('form_send_google_gmail_bcc_label')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors.bcc?.message}
                    />
                  );
                }}
              />
            </Grid2>

            <Grid2 size={12}>
              <Controller
                name='subject'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <BaseInput
                    value={value}
                    label={t('form_send_google_gmail_subject_label')}
                    onChange={onChange}
                    required
                    errorMessage={errors.subject?.message}
                  />
                )}
              />
            </Grid2>

            <Grid2 size={12}>
              <Controller
                name='content'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <BaseTextEditor
                    mode={ModeTextEditorEnum.CREATE}
                    height={200}
                    label={t('form_send_google_gmail_content_label')}
                    value={value}
                    onChange={(textEditor: string) => onChange(textEditor)}
                    required
                    errorMessage={errors.content?.message}
                  />
                )}
              />
            </Grid2>

            <Controller
              name='files'
              control={control}
              render={({ field: { onChange } }) => (
                <FileUpload
                  multiple
                  accept={ACCEPT_ALL_FILES}
                  onChange={onChange}
                  errorMessage={errors.files?.message}
                />
              )}
            />
          </Grid2>
        </DialogContent>

        <DialogActions className={cx('actionsForm')}>
          <Button variant='outlined' onClick={handleCloseForm}>
            {t('common_action_cancel', { ns: 'common/base' })}
          </Button>

          <Button variant='contained' type='submit'>
            {t('common_action_save', { ns: 'common/base' })}
          </Button>
        </DialogActions>

        {isLoading && <Loading />}
      </Box>
    </Dialog>
  );
};

export default FormSendEmail;
